import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../../api/client";

type Guest = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  plus_one: boolean;
  plus_one_first_name: string | null;
  plus_one_last_name: string | null;
  plus_one_email: string | null;
  plus_one_phone: string | null;
  plus_one_address: string | null;
  rsvp_status: string;
  plus_one_rsvp_status: string;
  dinner_choice_label: string | null;
  plus_one_dinner_choice_label: string | null;
  children: ChildGuest[];
};

type ChildGuest = {
  id: string;
  first_name: string;
  last_name: string;
  rsvp_status: string;
  dinner_choice_label: string | null;
};

type DisplayRow = {
  id: string;
  guestId: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  rsvp_status: string;
  dinner_choice_label: string | null;
  isPlusOne: boolean;
  primaryGuestName?: string;
  isChild: boolean;
};

type EditingGuest = Guest | null;
type ChildDraft = { id?: string; firstName: string; lastName: string };

type SortKey = "last_name" | "first_name" | "email" | "phone" | "rsvp_status";

function guestsToDisplayRows(guests: Guest[]): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const g of guests) {
    // Primary guest row
    rows.push({
      id: g.id,
      guestId: g.id,
      first_name: g.first_name,
      last_name: g.last_name,
      email: g.email,
      phone: g.phone,
      address: g.address,
      rsvp_status: g.rsvp_status,
      dinner_choice_label: g.dinner_choice_label,
      isPlusOne: false,
      isChild: false,
    });
    // Plus one row (if exists)
    if (g.plus_one) {
      rows.push({
        id: `${g.id}-plusone`,
        guestId: g.id,
        first_name: g.plus_one_first_name ?? "",
        last_name: g.plus_one_last_name ?? "",
        email: g.plus_one_email,
        phone: g.plus_one_phone,
        address: g.plus_one_address,
        rsvp_status: g.plus_one_rsvp_status,
        dinner_choice_label: g.plus_one_dinner_choice_label,
        isPlusOne: true,
        primaryGuestName: `${g.first_name} ${g.last_name}`,
        isChild: false,
      });
    }
    for (const child of g.children ?? []) {
      rows.push({
        id: child.id,
        guestId: g.id,
        first_name: child.first_name,
        last_name: child.last_name,
        email: null,
        phone: null,
        address: null,
        rsvp_status: child.rsvp_status,
        dinner_choice_label: child.dinner_choice_label,
        isPlusOne: false,
        isChild: true,
        primaryGuestName: `${g.first_name} ${g.last_name}`,
      });
    }
  }
  return rows;
}

export default function Guests() {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("last_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [editing, setEditing] = useState<EditingGuest>(null);
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const load = () =>
    api
      .get<{ guests: Guest[] }>("/api/admin/guests")
      .then((r) => setGuests(r.guests));

  useEffect(() => {
    void load();
  }, []);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir("asc");
    }
  }

  const displayRows = useMemo(() => guestsToDisplayRows(guests), [guests]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return displayRows;
    return displayRows.filter((row) =>
      [
        row.first_name,
        row.last_name,
        row.email,
        row.phone,
        row.address,
        row.primaryGuestName,
        row.rsvp_status,
        row.dinner_choice_label,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [displayRows, search]);

  const sorted = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const av = String(a[sortKey] ?? "").toLowerCase();
      const bv = String(b[sortKey] ?? "").toLowerCase();
      return av.localeCompare(bv) * (sortDir === "asc" ? 1 : -1);
    });
    return copy;
  }, [filteredRows, sortKey, sortDir]);

  function exportGuests() {
    const rows = guests.map((guest) => ({
      ID: guest.id,
      "First Name": guest.first_name,
      "Last Name": guest.last_name,
      Email: guest.email ?? "",
      Phone: guest.phone ?? "",
      Address: guest.address ?? "",
      "Plus One": guest.plus_one ? "Yes" : "No",
      "Plus One First Name": guest.plus_one_first_name ?? "",
      "Plus One Last Name": guest.plus_one_last_name ?? "",
      "Plus One Email": guest.plus_one_email ?? "",
      "Plus One Phone": guest.plus_one_phone ?? "",
      "Plus One Address": guest.plus_one_address ?? "",
      Children: (guest.children ?? [])
        .map((child) => `${child.first_name} ${child.last_name}`)
        .join("; "),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Guests");
    XLSX.writeFile(workbook, "wedding-guests.xlsx");
  }

  async function importGuests(file: File) {
    setImporting(true);
    setImportMessage(null);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!firstSheet) throw new Error("The workbook does not contain a sheet.");
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet, {
        defval: "",
      });
      if (rows.length === 0) throw new Error("The workbook does not contain any guests.");

      let created = 0;
      let updated = 0;
      for (const row of rows) {
        const value = (...keys: string[]) => {
          const key = Object.keys(row).find((name) =>
            keys.some((candidate) => name.trim().toLowerCase() === candidate.toLowerCase())
          );
          return key ? String(row[key] ?? "").trim() : "";
        };
        const firstName = value("First Name", "first_name");
        const lastName = value("Last Name", "last_name");
        if (!firstName || !lastName) {
          throw new Error("Every imported row needs a first and last name.");
        }

        const plusOneFirstName = value("Plus One First Name", "plus_one_first_name");
        const plusOneLastName = value("Plus One Last Name", "plus_one_last_name");
        const plusOneValue = value("Plus One", "plus_one").toLowerCase();
        const plusOne = ["yes", "true", "1", "x"].includes(plusOneValue) ||
          !!plusOneFirstName ||
          !!plusOneLastName;
        const children = value("Children", "children")
          .split(";")
          .map((child) => child.trim())
          .filter(Boolean)
          .map((child) => {
            const [childFirstName, ...lastNameParts] = child.split(/\s+/);
            return { firstName: childFirstName, lastName: lastNameParts.join(" ") };
          });
        if (children.some((child) => !child.firstName || !child.lastName)) {
          throw new Error(`Each child for ${firstName} ${lastName} needs a first and last name.`);
        }

        const importedId = value("ID", "id");
        const existing = guests.find(
          (guest) =>
            (importedId && guest.id === importedId) ||
            (guest.first_name.toLowerCase() === firstName.toLowerCase() &&
              guest.last_name.toLowerCase() === lastName.toLowerCase())
        );
        const data = {
          firstName,
          lastName,
          email: value("Email", "email"),
          phone: value("Phone", "phone"),
          address: value("Address", "address"),
          plusOne,
          plusOneFirstName,
          plusOneLastName,
          plusOneEmail: value("Plus One Email", "plus_one_email"),
          plusOnePhone: value("Plus One Phone", "plus_one_phone"),
          plusOneAddress: value("Plus One Address", "plus_one_address"),
          children,
        };
        if (existing) {
          await api.put(`/api/admin/guests/${existing.id}`, data);
          updated += 1;
        } else {
          await api.post("/api/admin/guests", data);
          created += 1;
        }
      }
      await load();
      setImportMessage(`Imported ${created + updated} guests (${created} new, ${updated} updated).`);
    } catch (err: any) {
      setImportMessage(err.message || "Unable to import guests.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="admin guests">
      <header className="admin-header">
        <h1 className="admin-heading">Guests</h1>
        <div className="guest-tools">
          <strong>{displayRows.length} guests</strong>
          <button className="button secondary" type="button" onClick={exportGuests}>
            Export Excel
          </button>
          <label className="button secondary guest-import-button">
            {importing ? "Importing..." : "Import Excel"}
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={importing}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void importGuests(file);
              }}
            />
          </label>
        </div>
      </header>

      {importMessage && <p className="muted guest-import-message">{importMessage}</p>}

      <NewGuestForm onSaved={load} />

      {editing && (
        <EditGuestModal
          guest={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <label className="guest-search">
        Search guests
        <input
          type="search"
          placeholder="Search by name, email, phone, or address"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      <div className="guest-table-scroll">
      <table className="guest-table">
        <thead>
          <tr>
            {(
              [
                ["last_name", "Last"],
                ["first_name", "First"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["rsvp_status", "RSVP"],
              ] as [SortKey, string][]
            ).map(([k, label]) => (
              <th key={k} onClick={() => toggleSort(k)} className="sortable">
                {label}
                {sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
            <th>Address</th>
            <th>Dinner</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const guest = guests.find((g) => g.id === row.guestId);
            return (
              <tr key={row.id} className={row.isPlusOne || row.isChild ? "plus-one-row" : ""}>
                <td>
                  {row.last_name}
                  {(row.isPlusOne || row.isChild) && (
                    <span className="plus-one-badge" title={`${row.isChild ? "Child" : "Plus one"} of ${row.primaryGuestName}`}>
                      {row.isChild ? "Child" : "+1"}
                    </span>
                  )}
                </td>
                <td>{row.first_name}</td>
                <td>{row.email ?? ""}</td>
                <td>{row.phone ?? ""}</td>
                <td>{row.rsvp_status}</td>
                <td>{row.address ?? ""}</td>
                <td>{row.dinner_choice_label ?? ""}</td>
                <td>
                  <div className="action-buttons">
                    {!row.isChild && guest && (
                      <>
                        <button className="edit" onClick={() => setEditing(guest)}>
                          Edit
                        </button>
                        {!row.isPlusOne && (
                          <button
                            className="delete"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `Remove ${guest.first_name} ${guest.last_name}${guest.plus_one ? " and their plus one" : ""}?`
                                )
                              )
                                return;
                              await api.del(`/api/admin/guests/${guest.id}`);
                              await load();
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={8} className="muted">
                No guests yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function NewGuestForm({ onSaved }: { onSaved: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [plusOne, setPlusOne] = useState(false);
  const [pFirst, setPFirst] = useState("");
  const [pLast, setPLast] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pAddress, setPAddress] = useState("");
  const [children, setChildren] = useState<ChildDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/admin/guests", {
        firstName,
        lastName,
        email,
        phone,
        address,
        plusOne,
        plusOneFirstName: pFirst,
        plusOneLastName: pLast,
        plusOneEmail: pEmail,
        plusOnePhone: pPhone,
        plusOneAddress: pAddress,
        children,
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setPlusOne(false);
      setPFirst("");
      setPLast("");
      setPEmail("");
      setPPhone("");
      setPAddress("");
      setChildren([]);
      onSaved();
    } catch (err: any) {
      setError(err.message || "Unable to add guest.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="admin-form">
      <label>
        First name
        <input
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
      </label>
      <label>
        Last name
        <input
          required
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </label>
      <label>
        Email (optional)
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label>
        Phone (optional)
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </label>
      <label className="wide">
        Address (optional)
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </label>
      <label className="plus-one check wide">
        <input
          type="checkbox"
          checked={plusOne}
          onChange={(e) => setPlusOne(e.target.checked)}
        />
        Include a plus one
      </label>
      {plusOne && (
        <>
          <label>
            Plus one first name (optional)
            <input value={pFirst} onChange={(e) => setPFirst(e.target.value)} />
          </label>
          <label>
            Plus one last name (optional)
            <input value={pLast} onChange={(e) => setPLast(e.target.value)} />
          </label>
          <label>
            Plus one email (optional)
            <input
              type="email"
              value={pEmail}
              onChange={(e) => setPEmail(e.target.value)}
            />
          </label>
          <label>
            Plus one phone (optional)
            <input
              type="tel"
              value={pPhone}
              onChange={(e) => setPPhone(e.target.value)}
            />
          </label>
          <label className="wide">
            Plus one address (optional)
            <input value={pAddress} onChange={(e) => setPAddress(e.target.value)} />
          </label>
        </>
      )}
      <div className="wide">
        {children.map((child, index) => (
          <div className="inline-fields" key={index}>
            <input
              required
              placeholder="First name"
              value={child.firstName}
              onChange={(e) => setChildren((current) => current.map((item, i) => i === index ? { ...item, firstName: e.target.value } : item))}
            />
            <input
              required
              placeholder="Last name"
              value={child.lastName}
              onChange={(e) => setChildren((current) => current.map((item, i) => i === index ? { ...item, lastName: e.target.value } : item))}
            />
            <button type="button" className="delete" onClick={() => setChildren((current) => current.filter((_, i) => i !== index))}>
              Remove child
            </button>
          </div>
        ))}
        <button type="button" className="button secondary" onClick={() => setChildren((current) => [...current, { firstName: "", lastName: "" }])}>
          Add child
        </button>
      </div>
      <div className="form-actions">
        <button className="button" type="submit" disabled={busy}>
          Add guest
        </button>
      </div>
      {error && <p className="error wide">{error}</p>}
    </form>
  );
}

function EditGuestModal({
  guest,
  onClose,
  onSaved,
}: {
  guest: Guest;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(guest.first_name);
  const [lastName, setLastName] = useState(guest.last_name);
  const [email, setEmail] = useState(guest.email ?? "");
  const [phone, setPhone] = useState(guest.phone ?? "");
  const [address, setAddress] = useState(guest.address ?? "");
  const [plusOne, setPlusOne] = useState(guest.plus_one);
  const [pFirst, setPFirst] = useState(guest.plus_one_first_name ?? "");
  const [pLast, setPLast] = useState(guest.plus_one_last_name ?? "");
  const [pEmail, setPEmail] = useState(guest.plus_one_email ?? "");
  const [pPhone, setPPhone] = useState(guest.plus_one_phone ?? "");
  const [pAddress, setPAddress] = useState(guest.plus_one_address ?? "");
  const [children, setChildren] = useState<ChildDraft[]>(
    (guest.children ?? []).map((child) => ({
      id: child.id,
      firstName: child.first_name,
      lastName: child.last_name,
    }))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/admin/guests/${guest.id}`, {
        firstName,
        lastName,
        email,
        phone,
        address,
        plusOne,
        plusOneFirstName: pFirst,
        plusOneLastName: pLast,
        plusOneEmail: pEmail,
        plusOnePhone: pPhone,
        plusOneAddress: pAddress,
        children,
      });
      onSaved();
    } catch (err: any) {
      setError(err.message || "Unable to save guest.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Guest</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={onSubmit} className="admin-form">
          <label>
            First name
            <input
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label>
            Last name
            <input
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <label>
            Email (optional)
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Phone (optional)
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="wide">
            Address (optional)
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </label>
          <label className="plus-one check wide">
            <input
              type="checkbox"
              checked={plusOne}
              onChange={(e) => setPlusOne(e.target.checked)}
            />
            Include a plus one
          </label>
          {plusOne && (
            <>
              <label>
                Plus one first name (optional)
                <input value={pFirst} onChange={(e) => setPFirst(e.target.value)} />
              </label>
              <label>
                Plus one last name (optional)
                <input value={pLast} onChange={(e) => setPLast(e.target.value)} />
              </label>
              <label>
                Plus one email (optional)
                <input
                  type="email"
                  value={pEmail}
                  onChange={(e) => setPEmail(e.target.value)}
                />
              </label>
              <label>
                Plus one phone (optional)
                <input
                  type="tel"
                  value={pPhone}
                  onChange={(e) => setPPhone(e.target.value)}
                />
              </label>
              <label className="wide">
                Plus one address (optional)
                <input value={pAddress} onChange={(e) => setPAddress(e.target.value)} />
              </label>
            </>
          )}
          <div className="wide">
            {children.map((child, index) => (
              <div className="inline-fields" key={child.id ?? index}>
                <input
                  required
                  placeholder="First name"
                  value={child.firstName}
                  onChange={(e) => setChildren((current) => current.map((item, i) => i === index ? { ...item, firstName: e.target.value } : item))}
                />
                <input
                  required
                  placeholder="Last name"
                  value={child.lastName}
                  onChange={(e) => setChildren((current) => current.map((item, i) => i === index ? { ...item, lastName: e.target.value } : item))}
                />
                <button type="button" className="delete" onClick={() => setChildren((current) => current.filter((_, i) => i !== index))}>
                  Remove child
                </button>
              </div>
            ))}
            <button type="button" className="button secondary" onClick={() => setChildren((current) => [...current, { firstName: "", lastName: "" }])}>
              Add child
            </button>
          </div>
          <div className="form-actions">
            <button type="button" className="button secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="button" type="submit" disabled={busy}>
              Save changes
            </button>
          </div>
          {error && <p className="error wide">{error}</p>}
        </form>
      </div>
    </div>
  );
}
